import { useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconChevronDown, IconDownload, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', status: '', dateFrom: '', dateTo: '' };

interface ChecklistEntry {
  id: number;
  item_name: string;
  is_done: boolean;
  completed_at: string | null;
}

interface SeparationRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  type: string;
  effective_date: string;
  reason: string | null;
  final_pay_amount: string | null;
  status: 'draft' | 'finalized';
  finalized_by_name: string | null;
  is_draft: boolean;
  has_attachment: boolean;
  attachment_filename: string | null;
  checklist: ChecklistEntry[];
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

const STATUS_BADGE: Record<SeparationRow['status'], string> = {
  draft: 'badge-scheduled',
  finalized: 'badge-active',
};

const emptyForm = { user_id: '' as string | number, type: '', effective_date: '', reason: '', final_pay_amount: '' };

export function Separation() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [addAttachment, setAddAttachment] = useState<File | null>(null);
  const [editing, setEditing] = useState<SeparationRow | null>(null);
  const [editForm, setEditForm] = useState({ type: '', effective_date: '', reason: '', final_pay_amount: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [calculating, setCalculating] = useState(false);

  // A starting point only — never applied without the officer seeing and
  // being able to edit it first, same "preview, don't silently decide"
  // shape the rest of this app uses (e.g. Asset's next-tag preview).
  const calculateFinalPay = async (userId: number | string, effectiveDate: string, apply: (amount: string) => void) => {
    if (!userId || !effectiveDate) {
      showError('Choose a separating staff member and effective date first.');
      return;
    }
    setCalculating(true);
    try {
      const res = await api.get<{ estimate: { total: number } | null }>('/separations/final-pay-estimate', {
        params: { user_id: userId, effective_date: effectiveDate },
      });
      if (!res.data.estimate) {
        showError('No current position/salary on record for this person — enter the amount manually.');
        return;
      }
      apply(String(res.data.estimate.total));
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Calculation failed.');
    } finally {
      setCalculating(false);
    }
  };

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canApprove = can('hr.approve');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<SeparationRow>('/separations', query);

  const openAdd = () => {
    setForm(emptyForm);
    setAddAttachment(null);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const data = new FormData();
      data.append('user_id', String(form.user_id));
      data.append('type', form.type);
      data.append('effective_date', form.effective_date);
      if (form.reason) data.append('reason', form.reason);
      if (form.final_pay_amount) data.append('final_pay_amount', form.final_pay_amount);
      if (addAttachment) data.append('attachment', addAttachment);
      await api.post('/separations', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: SeparationRow) => {
    setEditing(row);
    setEditForm({
      type: row.type,
      effective_date: row.effective_date,
      reason: row.reason ?? '',
      final_pay_amount: row.final_pay_amount ?? '',
    });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/separations/${editing.id}`, { ...editForm, final_pay_amount: editForm.final_pay_amount || undefined });
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const finalize = async (row: SeparationRow) => {
    if (!(await confirmAction(`Finalize ${row.user_name}'s separation? This cannot be undone.`, { confirmLabel: 'Finalize' })))
      return;
    try {
      await api.post(`/separations/${row.id}/finalize`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Finalize failed.');
    }
  };

  const remove = async (row: SeparationRow) => {
    if (!(await confirmAction(`Delete this separation record for ${row.user_name}?`))) return;
    try {
      await api.delete(`/separations/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleChecklistItem = async (entry: ChecklistEntry) => {
    try {
      await api.put(`/separation-checklist-entries/${entry.id}`, { is_done: !entry.is_done });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  const downloadAttachment = (row: SeparationRow) =>
    downloadFile(api, `/separations/${row.id}/attachment`, row.attachment_filename ?? `${row.user_name}-attachment`);

  const removeAttachment = async (row: SeparationRow) => {
    if (!(await confirmAction(`Remove the attachment for ${row.user_name}?`))) return;
    try {
      await api.delete(`/separations/${row.id}/attachment`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Remove failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Separation</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="finalized">Finalized</option>
        </select>
        <label className="checkbox-inline">
          Date from
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Effective</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.flatMap((row) => {
                  const isExpanded = expanded.has(row.id);
                  const rows = [
                    <tr key={row.id}>
                      <td>
                        <button
                          type="button"
                          className="btn icon-btn btn-ghost"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          onClick={() => toggleExpanded(row.id)}
                        >
                          <IconChevronDown
                            className="icon-btn-svg"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                          />
                        </button>
                      </td>
                      <td className="muted">{row.employee_code ?? '—'}</td>
                      <td>
                        <strong>{row.user_name}</strong>
                      </td>
                      <td>{labelFor(lists, 'separation_type', row.type)}</td>
                      <td>{row.effective_date}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[row.status]}`}>{row.status}</span>
                      </td>
                      <td className="right">
                        {canApprove && row.is_draft && (
                          <IconButton icon={IconCheck} label="Finalize" onClick={() => finalize(row)} />
                        )}
                        {canUpdate && row.is_draft && (
                          <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />
                        )}
                        {canArchive && row.is_draft && (
                          <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                        )}
                      </td>
                    </tr>,
                  ];

                  if (!isExpanded) return rows;

                  row.checklist.forEach((entry) => {
                    rows.push(
                      <tr key={`checklist-${entry.id}`}>
                        <td></td>
                        <td colSpan={4}>
                          <label className="checkbox-inline">
                            <input
                              type="checkbox"
                              checked={entry.is_done}
                              disabled={!canUpdate}
                              onChange={() => toggleChecklistItem(entry)}
                            />
                            {entry.item_name}
                          </label>
                        </td>
                        <td colSpan={2} className="muted">
                          {entry.completed_at ?? ''}
                        </td>
                      </tr>
                    );
                  });

                  if (row.checklist.length === 0) {
                    rows.push(
                      <tr key={`checklist-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={6} className="muted">
                          No checklist items configured.
                        </td>
                      </tr>
                    );
                  }

                  rows.push(
                    <tr key={`attachment-${row.id}`}>
                      <td></td>
                      <td colSpan={6}>
                        {row.has_attachment && (
                          <>
                            <IconButton icon={IconDownload} label={row.attachment_filename ?? 'Download'} onClick={() => downloadAttachment(row)} />
                            {canUpdate && (
                              <IconButton
                                icon={IconTrash}
                                label="Remove attachment"
                                variant="danger-ghost"
                                onClick={() => removeAttachment(row)}
                              />
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );

                  return rows;
                })
              ) : (
                <tr>
                  <td colSpan={7} className="muted center">
                    No separation records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add separation record" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <SearchableSelect<UserOption>
                endpoint="/users"
                value={form.user_id}
                onChange={(id) => setForm({ ...form, user_id: id })}
                getLabel={(u) => u.full_name}
                placeholder="Search staff…"
              />
              {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
            </label>
            <label className="span-2">
              Type *
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'separation_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.type && <small className="error">{errors.type[0]}</small>}
            </label>
            <label className="span-2">
              Effective date *
              <input
                type="date"
                value={form.effective_date}
                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                required
              />
              {errors.effective_date && <small className="error">{errors.effective_date[0]}</small>}
            </label>
            <label className="span-2">
              Final pay amount
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.final_pay_amount}
                  onChange={(e) => setForm({ ...form, final_pay_amount: e.target.value })}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={calculating}
                  onClick={() =>
                    calculateFinalPay(form.user_id, form.effective_date, (amount) =>
                      setForm((f) => ({ ...f, final_pay_amount: amount }))
                    )
                  }
                >
                  {calculating ? 'Calculating…' : 'Calculate'}
                </button>
              </div>
              <small className="muted">Partial final month's salary plus unused annual leave payout — a starting point you can still edit.</small>
              {errors.final_pay_amount && <small className="error">{errors.final_pay_amount[0]}</small>}
            </label>
            <label className="span-2">
              Reason
              <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </label>
            <label className="span-2">
              Attachment
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setAddAttachment(e.target.files?.[0] ?? null)}
              />
              {errors.attachment && <small className="error">{errors.attachment[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.user_id || !form.type || !form.effective_date}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit separation for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Type *
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                required
              >
                {listOptions(lists, 'separation_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.type && <small className="error">{errors.type[0]}</small>}
            </label>
            <label className="span-2">
              Effective date *
              <input
                type="date"
                value={editForm.effective_date}
                onChange={(e) => setEditForm({ ...editForm, effective_date: e.target.value })}
                required
              />
              {errors.effective_date && <small className="error">{errors.effective_date[0]}</small>}
            </label>
            <label className="span-2">
              Final pay amount
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.final_pay_amount}
                  onChange={(e) => setEditForm({ ...editForm, final_pay_amount: e.target.value })}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={calculating}
                  onClick={() =>
                    calculateFinalPay(editing.user_id, editForm.effective_date, (amount) =>
                      setEditForm((f) => ({ ...f, final_pay_amount: amount }))
                    )
                  }
                >
                  {calculating ? 'Calculating…' : 'Calculate'}
                </button>
              </div>
              <small className="muted">Partial final month's salary plus unused annual leave payout — a starting point you can still edit.</small>
              {errors.final_pay_amount && <small className="error">{errors.final_pay_amount[0]}</small>}
            </label>
            <label className="span-2">
              Reason
              <textarea
                rows={3}
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
